// Sincronización de la partida en red (host-autoritario).
// - Host: aplica las acciones (propias y de invitados) al Game y difunde el estado.
// - Invitado: envía sus acciones al host y renderiza el estado que recibe.
import { EVENTS } from '../utils/constants.js';

// Métodos del Game que un invitado puede solicitar (solo en su turno).
const ALLOWED = new Set([
  'placeTileFromRack', 'moveTileOnBoard', 'moveWord', 'returnTileToRack',
  'resetTurn', 'undo', 'redo', 'confirmTurn', 'drawAndPass', 'passTurn', 'resolveTimeout',
  'assignWildcard', 'splitTile', 'mergeTiles', 'transformTile',
]);

export class NetSync {
  constructor({ game, net, bus, isHost, selfPlayerId, hooks = {} }) {
    this.game = game;
    this.net = net;
    this.bus = bus;
    this.isHost = isHost;
    this.selfPlayerId = selfPlayerId;
    this.hooks = hooks; // { onState, onEnd, onConfirmResult }
    this._pending = false;
    this._off = net.onMessage((msg, from) => this._onMessage(msg, from));

    // El host difunde el estado tras cada cambio del tablero/turno.
    if (isHost) {
      this._offBus = [
        bus.on(EVENTS.BOARD_CHANGED, () => this._scheduleBroadcast()),
        bus.on(EVENTS.TURN_START, () => this._scheduleBroadcast()),
        bus.on(EVENTS.SCORE_UPDATED, () => this._scheduleBroadcast()),
      ];
    }
  }

  /** Ejecuta una acción: host aplica y difunde; invitado la envía al host. */
  commit(method, ...args) {
    if (this.isHost) {
      const r = this.game[method](...args);
      this._scheduleBroadcast();
      return r;
    }
    // Invitado: solo puede actuar en su turno.
    if (!this.game.isSelfTurn()) return false;
    this.net.sendToHost({ t: 'cmd', method, args, from: this.selfPlayerId });
    return true;
  }

  /** El host anuncia el fin de la partida a los invitados. */
  broadcastEnd(summary) {
    if (!this.isHost) return;
    this._broadcast();
    this.net.send({ t: 'end', summary });
  }

  /** Fuerza el envío inmediato del estado actual (p. ej. estado inicial). */
  pushState() { if (this.isHost) this._broadcast(); }

  _scheduleBroadcast() {
    if (!this.isHost || this._pending) return;
    this._pending = true;
    Promise.resolve().then(() => { this._pending = false; this._broadcast(); });
  }
  _broadcast() { this.net.send({ t: 'state', data: this.game.serialize() }); }

  _onMessage(msg, from) {
    if (!msg || !msg.t) return;
    if (this.isHost) {
      if (msg.t === 'cmd') {
        // Solo el jugador activo puede actuar, y solo métodos permitidos.
        if (!ALLOWED.has(msg.method)) return;
        if (this.game.activePlayer?.id !== msg.from) return;
        try { this.game[msg.method](...(msg.args || [])); } catch { /* ignora acción inválida */ }
        this._scheduleBroadcast();
      }
    } else {
      if (msg.t === 'state') {
        this.game.applyRemoteState(msg.data);
        this.hooks.onState?.(msg.data);
      } else if (msg.t === 'end') {
        this.hooks.onEnd?.(msg.summary);
      }
    }
  }

  dispose() {
    this._off?.();
    (this._offBus || []).forEach((o) => o?.());
  }
}
